import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from '@guildeducationinc/recess';
import { LANGUAGE_IMAGE_PATH } from './constants';
import { LanguagePanel } from './LanguagePanel';
import { useLanguagePreference } from './LanguagePreferenceProvider';
import styles from './LanguageDropdown.module.scss';
import ExpandMoreIcon from '@guildeducationinc/recess/icons/ExpandMoreIcon';
/**
 * Dropdown component for selecting language preferences with Weglot integration.
 * Shows current language with flag/label and opens a panel with available options.
 * Includes keyboard navigation, click-outside-to-close, and analytics tracking.
 */
export const LanguageDropdown = ({ id, className, }) => {
    const [open, setOpen] = useState(false);
    const [isWeglotInitialized, setIsWeglotInitialized] = useState(false);
    const buttonRef = useRef(null);
    const panelRef = useRef(null);
    const { currentLanguage, handleLanguageChange, languageOptions, languagePreferenceClickable } = useLanguagePreference();
    /**
     * Handles keyboard interactions for the dropdown button.
     */
    const handleOnKeyDown = (e) => {
        switch (e.key) {
            case 'Enter':
            case ' ':
                e.preventDefault();
                setOpen(!open);
                break;
            case 'Escape':
                e.preventDefault();
                setOpen(false);
                break;
        }
    };
    // Monitor Weglot initialization status
    useEffect(() => {
        const checkWeglotInitialization = () => {
            if (typeof window === 'undefined' || !window.Weglot) {
                setIsWeglotInitialized(false);
                return false;
            }
            const initialized = window.Weglot.initialized;
            setIsWeglotInitialized(!!initialized);
            return !!initialized;
        };
        // Check immediately
        // If already initialized, no need to set up interval
        if (checkWeglotInitialization()) {
            return;
        }
        // Set up interval to check for Weglot initialization
        const intervalId = setInterval(() => {
            if (checkWeglotInitialization()) {
                clearInterval(intervalId);
            }
        }, 500);
        // Clear interval after 10 seconds
        const timeoutId = setTimeout(() => {
            clearInterval(intervalId);
        }, 10000);
        return () => {
            clearInterval(intervalId);
            clearTimeout(timeoutId);
        };
    }, []);
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
            }
        };
        const handleOutsideClick = (e) => {
            if (!panelRef.current?.contains(e.target) &&
                !buttonRef.current?.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('keydown', handleEscapeKey);
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchend', handleOutsideClick);
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchend', handleOutsideClick);
        };
    }, []);
    // Only render if Weglot is properly initialized
    // TODO: Remove this comment when Weglot is enabled in production.
    // Currently, Weglot is disabled in production environments by passing an empty API key,
    // which prevents initialization. This conditional ensures the component gracefully
    // handles environments where Weglot is not available.
    if (!isWeglotInitialized) {
        return null;
    }
    /**
     * Handles button clicks and tracks analytics.
     */
    const handleOnClick = (language) => {
        languagePreferenceClickable(language.label, 'open_language_preference');
        setOpen(!open);
    };
    return (_jsxs("div", { className: classNames(styles.languageDropdown, className), children: [_jsxs(Button, { appearance: "ghost", "aria-controls": id, "aria-expanded": open, "aria-haspopup": true, className: styles.languageDropdownButton, forwardRef: buttonRef, id: `${id}-trigger`, onClick: () => handleOnClick(currentLanguage), onKeyDown: handleOnKeyDown, size: "small", children: [_jsx("img", { src: `${LANGUAGE_IMAGE_PATH}/icon-language.svg`, alt: "", className: styles.languageIcon }), _jsx("span", { className: styles.languageLabel, children: currentLanguage.label }), _jsx(ExpandMoreIcon, { className: styles.dropdownArrowIcon, color: "black", size: 18 })] }), open && (_jsx("div", { className: styles.panelContainer, children: _jsx(LanguagePanel, { handleLanguageChange: handleLanguageChange, languageOptions: languageOptions, panelRef: panelRef, setOpen: setOpen }) }))] }));
};
